import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  pn,
  ScreenType,
  Stack,
  useParams,
  useUnsavedChanges,
  useValidationErrors
} from "@compass/core";
import {
  Provider,
  ProviderEditResource,
  ProviderEmployment as ProviderEmploymentModel,
  ProviderViewResource,
  ProviderEmploymentViewResource,
  ProviderEmploymentEditResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { ProviderBasicInfo } from "../ProviderPage/ProviderBasicInfo";
import { ProviderEmployment } from "./ProviderEmployment";
import { ProviderPageHeader } from "./ProviderPageHeader";
import { ProviderEmploymentPageBody } from "pages/providers/provider-employments/ProviderEmploymentPage/ProviderEmploymentPageBody";


const routeName = "createProvider";

class Params {
  screenType: ScreenType = "standard";
}

class OptionalParams {
  closeOnSave?: boolean = false;
}

export const providerCreatePageRoute: PageRoute<Params, OptionalParams> = {
  handle: "New Provider",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export type ProviderCreateContextValue = {
  onCreate?: (id: number) => void;
};

export const ProviderCreateContext = createContext<ProviderCreateContextValue>({} as ProviderCreateContextValue);

export const ProviderCreatePage = () => {
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const [provider, setProvider] = useState<Provider>({} as Provider);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [provider] });
  const navigate = useNavigate();
  const [viewResource, setViewResource] = useState<ProviderViewResource>({});
  const [editResource, setEditResource] = useState<ProviderEditResource>({} as ProviderEditResource);
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [closeOnSave] = useState<boolean>(optionalParams.closeOnSave || false);

 const [employmentViewResource, setEmploymentViewResource] = useState<ProviderEmploymentViewResource>({});
const [employmentEditResource, setEmploymentEditResource] = useState<ProviderEmploymentEditResource>({});

  useEffect(() => {
    fetch(0);
    fetchViewResource(0);
    fetchEditResource();
  }, []);

  useEffect(() => {
  ApiService.providerEmployments.getViewResource().then((response) => {
    setEmploymentViewResource(response);
  });
  ApiService.providerEmployments.getEditResource().then((response) => {
    setEmploymentEditResource(response);
  });
}, []);

  const fetch = (id: number) => {
    ApiService.providers.get(id).then((response) => {
      setProvider(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.providers.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.providers.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <ProviderPageHeader
      handle={providerCreatePageRoute.handle}
      provider={provider}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
      closeOnSave={closeOnSave}
    />
  );

  const pageBody = (
    <Stack spacing="lg">
      <ProviderBasicInfo
        provider={provider}
        setProvider={setProvider}
        viewResource={viewResource}
        editResource={editResource}
        crudMode={"edit"}
        validationErrors={validationErrors}
      />

    <ProviderEmploymentPageBody
         crudMode="edit"
         employment={provider.creatingFirstEmployment || ({} as ProviderEmploymentModel)}
         setEmployment={(employment) =>
           setProvider(
                 produce((draft) => {
                     draft.creatingFirstEmployment = employment;
                 })
            )
         }
              viewResource={employmentViewResource}
              editResource={employmentEditResource}
              validationErrors={validationErrors[pn<Provider>("creatingFirstEmployment")] || {}}
          />
    </Stack>
  );
  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      {pageBody}
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      backgroundColor={(theme) => theme.palette.background.default}
      header={pageHeader}
      content={pageBody}
    />
  );
};
